<template>
  <v-container id="regular-tables" fluid tag="section">
    <base-v-component heading="기술 자료 관리" link="components/simple-tables" />

    <base-material-card icon="mdi-clipboard-text" title="기술 자료 목록" class="px-5 py-3">

      <!-- 분야별 탭 -->
      <v-tabs v-model="activeTab" background-color="primary" dark>
        <v-tab v-for="tab in tabs" :key="tab" :value="tab">{{ tab }}</v-tab>
      </v-tabs>

      <v-btn color="primary" class="mb-3 mt-3" @click="onCreate" v-if="isLogin">
        <v-icon left>mdi-plus</v-icon> 새 자료 등록
      </v-btn>

      <!-- 테이블 -->
      <v-simple-table>
        <thead>
          <tr>
            <th class="primary--text">ID</th>
            <th class="primary--text">제목</th>
            <th class="primary--text">분야</th>
            <th class="primary--text">등록일</th>
            <th class="text-right primary--text">작업</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in filteredItems"
            :key="item.libSeq"
            @click="openDetail(item)"
            style="cursor: pointer;"
          >
            <td>{{ item.libSeq }}</td>
            <td>{{ item.libTitle }}</td>
            <td>{{ item.libType }}</td>
            <td>{{ formatDate(item.regDt) }}</td>
            <td class="text-right" @click.stop>
              <v-btn small color="warning" class="mr-4" @click="onEdit(item.libSeq)">
                <v-icon left>mdi-pencil</v-icon> 수정
              </v-btn>
              <v-btn small color="error" @click="onDelete(item.libSeq)">
                <v-icon left>mdi-delete</v-icon> 삭제
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </base-material-card>

    <!-- 등록/수정 폼 다이얼로그 -->
    <v-dialog v-model="showForm" max-width="600px">
      <base-material-card :title="isEdit ? '자료 수정' : '자료 등록'" class="px-5 py-3">
        <v-form @submit.prevent="onSubmit">
          <v-text-field v-model="form.libTitle" label="제목" required />
          <v-select
            v-model="form.libType"
            :items="['BACKEND','FRONT','SERVER']"
            label="분야"
            required
          />
          <v-textarea v-model="form.libContent" label="내용" rows="6" required />

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" type="submit">{{ isEdit ? '수정' : '등록' }}</v-btn>
            <v-btn color="grey" @click="onCancel">취소</v-btn>
          </v-card-actions>
        </v-form>
      </base-material-card>
    </v-dialog>

    <!-- ✅ 상세보기 다이얼로그 -->
    <v-dialog v-model="showDetail" :fullscreen="isFullscreen" scrollable max-width="900px">
      <v-card>
        <v-toolbar dense flat color="primary" dark>
          <v-toolbar-title>{{ detailItem.libTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- 전체화면 토글 버튼 -->
          <v-btn icon @click="toggleFullscreen">
            <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          </v-btn>

          <!-- 닫기 버튼 -->
          <v-btn icon @click="closeDetail">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text style="max-height: 70vh; overflow-y: auto; overflow-x: auto;">
          <div><strong>번호:</strong> {{ detailItem.libSeq }}</div>
          <div><strong>제목:</strong> {{ detailItem.libTitle }}</div>
          <div><strong>분야:</strong> {{ detailItem.libType }}</div>
          <div><strong>등록일:</strong> {{ formatDate(detailItem.regDt) }}</div>
          <div><strong>내용:</strong></div>
          <pre style="white-space: pre-wrap;">{{ detailItem.libContent }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import TechLibraryApi from "./api/techLibraryApi";

export default {
  data() {
    return {
      activeTab: 0,
      tabs: ["ALL", "BACKEND", "FRONT", "SERVER"],
      items: [],
      showForm: false,
      isEdit: false,
      form: {
        libSeq: null,
        libTitle: "",
        libType: "BACKEND",
        libContent: "",
        regId: "admin"
      },
      // ✅ 상세보기용 데이터
      showDetail: false,
      detailItem: {},
      isFullscreen: false
    };
  },
  computed: {
    filteredItems() {
      const tabName = this.tabs[this.activeTab]; 
      if (tabName === "ALL") return this.items;
      return this.items.filter(item => item.libType === tabName);
    },
    isLogin() {
      return this.$store.state.user.isLogin;
    }
  },
  methods: {
    async loadData() {
      const res = await TechLibraryApi.getAll();
      this.items = Array.isArray(res.data) ? res.data : res.data.data;
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleString() : "";
    },
    // ✅ 상세보기 기능
    openDetail(item) {
      this.detailItem = { ...item };
      this.showDetail = true;
      this.isFullscreen = false;
    },
    closeDetail() {
      this.showDetail = false;
      this.detailItem = {};
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    onCreate() {
      this.isEdit = false;
      this.form = { libTitle: "", libType: "BACKEND", libContent: "", regId: "admin" };
      this.showForm = true;
    },
    async onEdit(libSeq) {
      const res = await TechLibraryApi.getById(libSeq);
      this.form = res.data;
      this.isEdit = true;
      this.showForm = true;
    },
    async onDelete(libSeq) {
      if (confirm("삭제하시겠습니까?")) {
        await TechLibraryApi.delete(libSeq);
        this.loadData();
      }
    },
    async onSubmit() {
      if (this.isEdit) {
        await TechLibraryApi.update(this.form.libSeq, this.form);
      } else {
        await TechLibraryApi.create(this.form);
      }
      this.showForm = false;
      this.loadData();
    },
    onCancel() {
      this.showForm = false;
    }
  },
  mounted() {
    if(this.isLogin){
      this.loadData();
    }
  }
};

</script>
